import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { usePlacesWidget } from "react-google-autocomplete";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import {
  FieldErrors,
  FieldValues,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  useForm,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import type { AnyObjectSchema } from "yup";

import style from "../surgeon.module.css";
import { addressSchema } from "../../../yup";
import { appConfig } from "../../../hooks/useConfig";
import { Input } from "../../../components/UserInput";
import { Button } from "../../../components";
import { colors } from "../../../theme/colors";

type FormState = typeof addressSchema.__outputType;
type PlaceComponent = {
  types: string[];
  short_name: string;
};
type PlaceGeometry = {
  location?: {
    lat?: (() => number) | number;
    lng?: (() => number) | number;
  };
};
type PlaceResultLike = {
  address_components?: PlaceComponent[];
  formatted_address?: string;
  geometry?: PlaceGeometry;
};

interface AddressFormProps {
  address?: AddressDetails;
  onCancel?: () => void;
  onSave?: (args: DeliveryAddressPayload) => void;
  isLoading?: boolean;
  formRegister?: UseFormRegister<FieldValues>;
  formSchema?: AnyObjectSchema;
  formErrors?: FieldErrors<FieldValues>;
  setFormValue?: UseFormSetValue<FieldValues>;
  clearFormErros?: UseFormClearErrors<FieldValues>;
  clearFormErrors?: UseFormClearErrors<FieldValues>;
  getFormValues?: UseFormGetValues<FieldValues>;
  // When true, visual required indicators (stars) are hidden for core address fields.
  addressFieldsOptional?: boolean;
  // Allows overriding required-star visibility on a per-field basis.
  // When provided, values take precedence over addressFieldsOptional.
  requiredConfig?: {
    suburb?: boolean;
    street_name?: boolean;
    state?: boolean;
    postcode?: boolean;
  };
}

/**
 * Renders a delivery address form with optional external form bindings for integrated parent validation.
 *
 * Args:
 *   address (AddressDetails | undefined): Prefills form when provided.
 *   onCancel (() => void | undefined): Invoked when the user cancels editing.
 *   onSave ((DeliveryAddressPayload) => void | undefined): Called with address payload after validation.
 *   isLoading (boolean | undefined): Disables actions while save is in progress.
 *   formRegister (any | undefined): Optional external register for parent-managed forms.
 *   formSchema (any | undefined): Optional validation schema override.
 *   formErrors (any | undefined): External error bag when embedded in parent form.
 *   setFormValue (any | undefined): Setter for parent form values when embedded.
 *   clearFormErros (any | undefined): Clears parent form errors; retains original naming.
 *   getFormValues (any | undefined): Getter for parent form values when embedded.
 *   clearFormErrors (any | undefined): Preferred spelling for clearing parent errors.
 *
 * Returns:
 *   JSX.Element: Address entry form.
 *
 * Notes:
 *   Uses Google Places autocomplete for address parsing; assumes AU country restriction.
 *   TODO: Confirm whether non-AU address parsing is required for future locales.
 */
const AddressForm = ({
  address,
  onCancel,
  onSave,
  isLoading,
  formRegister,
  formSchema,
  formErrors,
  setFormValue,
  clearFormErros,
  clearFormErrors,
  getFormValues,
  addressFieldsOptional = false,
  requiredConfig,
}: AddressFormProps) => {
  const [latitude, setLatitude] = useState(0);
  const [longitude, setLongitude] = useState(0);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
    reset,
    getValues
  } = useForm<FormState>({
    resolver: yupResolver(formSchema ?? addressSchema),
    mode: "onChange",
  });

  const { ref: materialRef } = usePlacesWidget({
    apiKey: appConfig?.GOOGLE_API_KEY,
    inputAutocompleteValue: "off",
    options: {
      types: [
        "street_address",
        "street_number",
        "intersection",
        "route",
        "health",
      ],
      componentRestrictions: { country: "au" },
    },
    // Google Places data can be incomplete; defensive parsing fills missing street fields.
    onPlaceSelected: (place: PlaceResultLike | null) => {
      const clearAddressErrors = clearFormErrors ?? clearFormErros;
      let suburb = "";
      let state = "";
      let postal = "";
      let streetName = '';
      let streetNumber = '';
      if (place) {
        if (clearAddressErrors) {
          clearAddressErrors("postcode");
          clearAddressErrors("suburb");
          clearAddressErrors("state");
        } else {
          clearErrors("postcode");
          clearErrors("suburb");
          clearErrors("state");
        }
        const address_comp = place?.address_components;
        if (address_comp) {
          for (const c of address_comp) {
            const types: Array<string> = c.types;
            if (
              !suburb &&
              (types.includes("locality") ||
                types.includes("sublocality") ||
                types.includes("sublocality_level_1") ||
                types.includes("postal_town") ||
                types.includes("administrative_area_level_3"))
            ) {
              suburb = c.short_name;
            }

            if (types.includes("administrative_area_level_1")) {
              state = c.short_name;
            } else if (types.includes("postal_code")) {
              postal = c.short_name;
            }
            if (types.includes('street_number')) {
              streetNumber = c.short_name;
            }
            if (types.includes('route')) {
              streetName = c.short_name;
            }
          }
          // Fall back to formatted_address parsing when components are missing.
          if (streetName === '' || streetNumber === '') {
            const match = place?.formatted_address?.match(/^(\d+)\s+(.+?),/);
            if (match) {
              streetNumber = match[1];
              streetName = match[2];
            } else {
              const addressArr = place?.formatted_address?.split(", ") ?? [];
              if (addressArr.length > 3) {
                streetName = addressArr.slice(0, 2).join(", ");
              } else {
                streetName = addressArr[0];
              }
            }
          }
          // geometry.location may be functions or numbers; normalize defensively.
          const latValue = get(place, "geometry.location.lat");
          const lngValue = get(place, "geometry.location.lng");
          const lat =
            typeof latValue === "function" ? latValue() : latValue;
          const lng =
            typeof lngValue === "function" ? lngValue() : lngValue;
          setLatitude(typeof lat === "number" ? lat : 0);
          setLongitude(typeof lng === "number" ? lng : 0);
          // If embedded in a parent form, use parent setters to keep a single source of truth.
          if (setFormValue) {
            if (getFormValues && getFormValues('street_number') === '') {
              setFormValue("street_number", streetNumber);
            }
            setFormValue("street_name", streetName);
            setFormValue("suburb", suburb);
            setFormValue("state", state);
            setFormValue("postcode", postal);
          } else {
            if (getValues && getValues('street_number') === '') {
              setValue("street_number", streetNumber);
            }
            setValue("street_name", streetName);
            setValue("suburb", suburb);
            setValue("state", state);
            setValue("postcode", postal);
          }
        }
      }
    },
  });

  const isRequired = (field: keyof NonNullable<typeof requiredConfig>) =>
    requiredConfig && typeof requiredConfig[field] === "boolean"
      ? !!requiredConfig[field]
      : !addressFieldsOptional;

  useEffect(() => {
    if (!isEmpty(address)) {
      reset({
        unit_number: address?.unit_number ?? "",
        street_number: address?.street_number ?? "",
        street_name: address?.street_name ?? "",
        postcode: address?.postcode ?? "",
        state: address?.state ?? "",
        suburb: address?.suburb ?? "",
        mail: address?.mail ?? "",
        phone: address?.phone ?? "",
        fax: address?.fax ?? "",
        provider_number: address?.provider_number ?? "",
      });
      setLatitude(address?.latitude ?? 0);
      setLongitude(address?.longitude ?? 0);
    }
  }, [address, reset]);

  const onSubmit = async (values: FormState) => {
    let payload: DeliveryAddressPayload = {
      unit_number: values.unit_number,
      street_number: values.street_number,
      street_name: values.street_name,
      suburb: values.suburb,
      state: values.state,
      postcode: values.postcode,
      latitude: latitude,
      longitude: longitude,
      mail: values.mail,
      phone: values.phone,
      provider_number: values.provider_number,
      fax: values.fax,
    };
    if (onSave) {
      onSave(payload);
    }
  };

  return (
    <Box>
      <Box className={style.flexTop}>
        <Box className={style.row}>
          <Input
            placeholder="Unit Number"
            label="Unit Number"
            register={formRegister ?? register}
            name="unit_number"
            errors={formErrors ?? errors}
          />
          <Input
            placeholder="Street Number"
            label="Street Number"
            register={formRegister ?? register}
            name="street_number"
            errors={formErrors ?? errors}
          />
        </Box>
        <Box className={style.row}>
          <Input
            placeholder="Street Name"
            label="Street Name"
            register={formRegister ?? register}
             // Street name star follows global optionality unless overridden.
            required={isRequired("street_name")}
            name="street_name"
            errors={formErrors ?? errors}
            inputRef={materialRef}
          />
          <Input
            placeholder="Enter suburb"
            label="Suburb"
            // Suburb can be forced required via requiredConfig, even when others are optional.
            required={isRequired("suburb")}
            register={formRegister ?? register}
            name="suburb"
            errors={formErrors ?? errors}
          />
        </Box>
        <Box className={style.row}>
          <Input
            placeholder="Enter state"
            label="State"
            required={isRequired("state")}
            register={formRegister ?? register}
            name="state"
            errors={formErrors ?? errors}
          />
          <Input
            placeholder="Enter postcode"
            label="Postcode"
            required={isRequired("postcode")}
            register={formRegister ?? register}
            name="postcode"
            errors={formErrors ?? errors}
          />
        </Box>
        <Box className={style.row}>
          <Input
            placeholder="Enter email"
            label="Email"
            register={formRegister ?? register}
            name="mail"
            errors={formErrors ?? errors}
          />
          <Input
            placeholder="Enter telephone number"
            label="Telephone Number"
            register={formRegister ?? register}
            name="phone"
            errors={formErrors ?? errors}
          />
        </Box>
        <Box className={style.row}>
          <Input
            placeholder="Enter fax number"
            label="Fax Number"
            register={formRegister ?? register}
            name="fax"
            errors={formErrors ?? errors}
          />
          <Input
            placeholder="Enter provider number"
            label="Provider Number"
            register={formRegister ?? register}
            name="provider_number"
            errors={formErrors ?? errors}
          />
        </Box>
        {!formRegister && (
          <Box className={style.buttons} sx={{ marginBottom: "20px" }}>
            <Button
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: "transparent" },
              }}
              variant="outlined"
              onClick={() => {
                if (onCancel) {
                  onCancel();
                }
              }}
              label={"Cancel"}
            />
            <Button
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: colors.primary },
              }}
              variant="contained"
              label={"Save"}
              type="submit"
              loading={isLoading}
              onClick={handleSubmit(onSubmit)}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AddressForm;
